import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { readLogAPI, parentalControlsAPI, mediaAPI, narrativeProgressAPI, narrativeAPI, resolveMediaSrc } from '@/lib/api';
import { ArrowLeft, ArrowRight, Clock, BookOpen, CheckCircle, AlertTriangle, Eye, Mic, Lock, Video, Shield, Music, Play, Pause, Volume2, Loader2, Image, Palette, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import WrittenAnswerModal from './WrittenAnswerModal';
import VocabularyAssessment from './VocabularyAssessment';
import WordDefinitionModal from './WordDefinitionModal';
import ReadAloudRecorder from '@/components/ReadAloudRecorder';
import SaveOfflineButton from '@/components/SaveOfflineButton';
import useReadingHeartbeat from '@/hooks/useReadingHeartbeat';
import ReflectionQuestionModal from './ReflectionQuestionModal';

const C = {
  bg: '#0A0F1E', surface: '#111827', card: '#1A2236',
  gold: '#D4A853', goldLight: '#F5D799', teal: '#38BDF8',
  cream: '#F8F5EE', muted: '#94A3B8', reading: '#E8E0D0',
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const extractYTId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};

const InlineMediaPlayer = ({ mediaId, title, mediaPlacements = [], studentId }) => {
  const [playing, setPlaying] = useState(false);
  const [audioEl, setAudioEl] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  const media = mediaPlacements.find(m => m.id === mediaId);
  if (!media) return <span className="italic text-sm" style={{ color: C.gold }}>[{title}]</span>;

  const isVideo = media.media_type === 'video' || media.youtube_url;
  const ytId = extractYTId(media.youtube_url);

  const handlePlay = async () => {
    if (isVideo) { setShowVideo(!showVideo); return; }
    if (playing) { audioEl?.pause(); setPlaying(false); return; }
    if (audioEl) audioEl.pause();
    let src;
    try { src = await resolveMediaSrc(media.file_url); } catch (e) { toast.info(e.userMessage || 'This song is not available right now.'); return; }
    const a = new Audio(src);
    a.play();
    a.onended = () => setPlaying(false);
    setAudioEl(a);
    setPlaying(true);
    if (studentId) mediaAPI.recordListen(studentId, mediaId).catch(() => {});
  };

  return (
    <span className="inline-block my-2">
      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-all hover:scale-105"
        style={{ background: 'rgba(212,168,83,0.15)', border: '1px solid rgba(212,168,83,0.3)' }}
        onClick={handlePlay}
        data-testid={`inline-media-${mediaId}`}>
        {isVideo ? <Video size={14} style={{ color: C.gold }} /> : playing ? <Pause size={14} style={{ color: C.gold }} /> : <Play size={14} style={{ color: C.gold }} />}
        <span className="text-sm font-semibold" style={{ color: C.gold }}>{media.title}</span>
        {media.artist && <span className="text-xs" style={{ color: C.muted }}>by {media.artist}</span>}
      </span>
      {showVideo && ytId && (
        <span className="block mt-2 w-full aspect-video">
          <iframe src={`https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1`}
            className="w-full h-full rounded-lg" style={{ border: `2px solid ${C.gold}` }}
            allow="autoplay; encrypted-media" allowFullScreen title={media.title} />
        </span>
      )}
    </span>
  );
};


// TTS player — streams MP3 from the backend (ElevenLabs multilingual). The
// browser SpeechSynthesis path is removed: it sounded inauthentic for
// non-English stories because OS voices are accent-locked.
const TTSPlayer = ({ narrativeId, chapterNumber, chapterText, voice = 'rachel', onWordChange, onPlayStateChange }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [rate, setRate] = useState(0.9);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = React.useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  // <audio> cannot send a login header, so playback carries a short-lived signed token.
  const [audioToken, setAudioToken] = useState(null);
  React.useEffect(() => {
    let live = true;
    setAudioToken(null);
    if (narrativeId) {
      narrativeAPI.audioToken(narrativeId).then((r) => { if (live) setAudioToken(r.data.token); }).catch(() => {});
    }
    return () => { live = false; };
  }, [narrativeId]);
  const audioSrc = narrativeId && chapterNumber && audioToken
    ? `${BACKEND_URL}/api/narratives/${narrativeId}/chapters/${chapterNumber}/tts?voice=${encodeURIComponent(voice)}&t=${encodeURIComponent(audioToken)}`
    : null;

  // Stop and reset when chapter or voice changes
  React.useEffect(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
    setIsPlaying(false);
    setLoading(false);
    setError(null);
    onPlayStateChange?.(false);
    onWordChange?.(-1);
  }, [narrativeId, chapterNumber, voice]); // eslint-disable-line

  // Keep playbackRate in sync
  React.useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate]);

  // Approximate word highlighting from playback progress. The backend MP3
  // has no per-word timing, so we map currentTime/duration → word index.
  const totalWords = React.useMemo(() => {
    const clean = (chapterText || '').replace(/\[MEDIA:[^\]]+\]/g, '').trim();
    return clean ? clean.split(/\s+/).length : 0;
  }, [chapterText]);

  const handleTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration || !totalWords) return;
    const idx = Math.min(totalWords - 1, Math.floor((a.currentTime / a.duration) * totalWords));
    onWordChange?.(idx);
  };

  const handlePlayPause = async () => {
    const a = audioRef.current;
    if (!a || !audioSrc) return;

    if (isPlaying) {
      a.pause();
      setIsPlaying(false);
      onPlayStateChange?.(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // First play kicks off backend generation; ElevenLabs can take 3–15s
      // for a fresh chapter, then it's instant from cache.
      await a.play();
      setIsPlaying(true);
      onPlayStateChange?.(true);
    } catch (e) {
      console.error('TTS playback failed', e);
      setError('Audio failed to load. Try again.');
      toast.error('Audio failed to load');
    } finally {
      setLoading(false);
    }
  };

  if (!audioSrc) return null;

  return (
    <div className="p-3 rounded-xl mb-4" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)' }}
      data-testid="tts-player">
      <div className="flex items-center gap-2 mb-2">
        <Volume2 size={16} style={{ color: C.teal }} />
        <p className="text-xs font-bold uppercase" style={{ color: C.teal }}>Listen to Story</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <audio
          ref={audioRef}
          src={audioSrc}
          preload="none"
          onEnded={() => { setIsPlaying(false); onPlayStateChange?.(false); onWordChange?.(-1); }}
          onTimeUpdate={handleTimeUpdate}
          onError={() => { setError('Audio failed to load'); setLoading(false); setIsPlaying(false); onPlayStateChange?.(false); }}
        />
        <button onClick={handlePlayPause}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105 disabled:opacity-60"
          style={{ background: isPlaying ? 'rgba(56,189,248,0.25)' : 'rgba(56,189,248,0.15)', color: C.teal, border: '1px solid rgba(56,189,248,0.3)' }}
          data-testid="tts-play-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : (isPlaying ? <Pause size={14} /> : <Play size={14} />)}
          {loading ? 'Preparing audio…' : (isPlaying ? 'Stop' : 'Read Aloud')}
        </button>
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-semibold" style={{ color: C.muted }}>Speed</span>
          <input type="range" min="0.5" max="1.5" step="0.1" value={rate}
            onChange={(e) => setRate(parseFloat(e.target.value))}
            className="w-16 h-1 accent-cyan-400"
            data-testid="tts-speed" />
          <span className="text-[10px] font-mono" style={{ color: C.muted }}>{rate}x</span>
        </div>
        {error && <span className="text-[10px] text-rose-400 font-bold">{error}</span>}
      </div>
    </div>
  );
};

const IllustrationPanel = ({ narrativeId, chapterNumber, hasPicture }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  // The picture comes from our own server (never straight from an image service), with a short-lived signed link.
  const [token, setToken] = useState(null);
  React.useEffect(() => {
    let live = true;
    setToken(null); setImageLoaded(false); setImageError(false);
    if (narrativeId) narrativeAPI.audioToken(narrativeId).then((r) => { if (live) setToken(r.data.token); }).catch(() => { if (live) setImageError(true); });
    return () => { live = false; };
  }, [narrativeId, chapterNumber]);

  if (!hasPicture || !narrativeId) return null;
  const imageUrl = token
    ? `${process.env.REACT_APP_BACKEND_URL || ''}/api/narratives/${narrativeId}/chapters/${chapterNumber}/illustration?t=${encodeURIComponent(token)}`
    : null;

  return (
    <div className="rounded-xl mb-4 overflow-hidden" style={{ border: '1px solid rgba(212,168,83,0.2)' }}
      data-testid="illustration-panel">
      {!imageError ? (
        <div className="relative">
          {!imageLoaded && (
            <div className="flex items-center justify-center py-8" style={{ background: 'rgba(212,168,83,0.05)' }}>
              <div className="flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" style={{ color: C.gold }} />
                <p className="text-xs font-bold" style={{ color: C.gold }}>Generating illustration...</p>
              </div>
            </div>
          )}
          <img
            src={imageUrl}
            alt="Story illustration"
            className={`w-full rounded-xl ${imageLoaded ? 'block' : 'hidden'}`}
            style={{ maxHeight: '300px', objectFit: 'cover' }}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        </div>
      ) : null}
    </div>
  );
};

const DYSLEXIA_OVERLAYS = [
  { name: 'Cream', color: '#FFF9E6' },
  { name: 'Blue', color: '#E6F3FF' },
  { name: 'Pink', color: '#FFE6F0' },
  { name: 'Green', color: '#E6FFE6' },
];

// Reader page backgrounds — applied to the page area around the reading card.
// The card itself stays dark so text contrast is unchanged. Keep this in sync
// with the picker preview swatches below.
const READER_BACKGROUNDS = [
  { id: 'midnight',  name: 'Midnight',       value: '#0A0F1E' },
  { id: 'library',   name: 'Cozy Library',   value: 'linear-gradient(135deg, #3C2415 0%, #5C3820 100%)' },
  { id: 'forest',    name: 'Forest Glade',   value: 'linear-gradient(135deg, #0F3D2C 0%, #1F5C44 100%)' },
  { id: 'ocean',     name: 'Ocean Deep',     value: 'linear-gradient(135deg, #0A2B5E 0%, #1E5BAE 100%)' },
  { id: 'sunset',    name: 'Sunset',         value: 'linear-gradient(135deg, #C2410C 0%, #DB2777 50%, #7C3AED 100%)' },
  { id: 'lavender',  name: 'Lavender Dream', value: 'linear-gradient(135deg, #4C1D95 0%, #7E22CE 100%)' },
  { id: 'cherry',    name: 'Cherry Blossom', value: 'linear-gradient(135deg, #831843 0%, #BE185D 60%, #DB2777 100%)' },
  { id: 'starlight', name: 'Starlight',      value: 'radial-gradient(ellipse at top, rgba(212,168,83,0.18) 0%, transparent 55%), radial-gradient(ellipse at bottom, rgba(56,189,248,0.12) 0%, transparent 55%), #0A0F1E' },
  { id: 'aurora',    name: 'Aurora',         value: 'linear-gradient(135deg, #0F766E 0%, #1E40AF 50%, #6D28D9 100%)' },
  { id: 'parchment', name: 'Parchment',      value: 'linear-gradient(135deg, #78350F 0%, #A16207 100%)' },
];

const readerBgStorageKey = (studentId) => `sv_reader_bg_${studentId || 'anon'}`;
const loadReaderBg = (studentId) => {
  try {
    const stored = localStorage.getItem(readerBgStorageKey(studentId));
    if (stored) {
      const match = READER_BACKGROUNDS.find((b) => b.id === stored);
      if (match) return match;
    }
  } catch { /* localStorage may be blocked — fall through */ }
  return READER_BACKGROUNDS[0];
};

const NarrativeReader = ({ narrative, student, onClose }) => {
  const queryClient = useQueryClient();
  const [currentChapter, setCurrentChapter] = useState(narrative.current_chapter || 1);
  const [sessionStart] = useState(new Date());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showWrittenCheck, setShowWrittenCheck] = useState(false);
  const [showReflection, setShowReflection] = useState(false);
  const [pendingAdvance, setPendingAdvance] = useState(null);  // action to run after reflection closes
  const [showAssessment, setShowAssessment] = useState(false);
  const [completedChapters, setCompletedChapters] = useState(narrative.chapters_completed || []);
  const [selectedWord, setSelectedWord] = useState(null);
  const [wordContext, setWordContext] = useState('');
  const [showRecorder, setShowRecorder] = useState(false);
  const [recordingDone, setRecordingDone] = useState(false);
  const [recordingBlobUrl, setRecordingBlobUrl] = useState(null);
  const [highlightedWordIndex, setHighlightedWordIndex] = useState(-1);
  const [difficultyLoading, setDifficultyLoading] = useState(false);
  const [difficultySubmitted, setDifficultySubmitted] = useState(null);
  const [currentNarrative, setCurrentNarrative] = useState(narrative);
  const readingPausedSecondsRef = React.useRef(0);
  const ttsPauseStartRef = React.useRef(null);

  // Dyslexia-friendly reading mode
  const hasDyslexia = (student?.accessibility_needs || []).includes('dyslexia');
  const [dyslexiaMode, setDyslexiaMode] = useState(hasDyslexia);
  const [dyslexiaOverlay, setDyslexiaOverlay] = useState(DYSLEXIA_OVERLAYS[0].color);
  const [readingRuler, setReadingRuler] = useState(false);
  const [rulerY, setRulerY] = useState(-100);

  // Reader background — remembered per student via localStorage.
  const [readerBg, setReaderBg] = useState(() => loadReaderBg(student?.id));
  const [showBgPicker, setShowBgPicker] = useState(false);
  useEffect(() => {
    try { localStorage.setItem(readerBgStorageKey(student?.id), readerBg.id); } catch { /* noop */ }
  }, [readerBg, student?.id]);

  // Reading ruler mouse/touch tracking
  useEffect(() => {
    if (!readingRuler) return;
    const handleMove = (e) => {
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      setRulerY(y);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('touchmove', handleMove);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
    };
  }, [readingRuler]);

  // Fetch parental controls
  const { data: parentalControls } = useQuery({
    queryKey: ['parental-controls', student?.id],
    queryFn: async () => (await parentalControlsAPI.get(student?.id)).data,
    enabled: !!student?.id,
  });

  const controls = parentalControls || { recording_mode: 'none', chapter_threshold: 0, can_skip_recording: true, auto_start_recording: false, require_confirmation: true };
  const isRecordingRequired = controls.recording_mode !== 'none';
  const meetsThreshold = controls.chapter_threshold === 0 || currentChapter >= controls.chapter_threshold;
  const mustRecord = isRecordingRequired && meetsThreshold;
  const canProceed = !mustRecord || recordingDone || controls.can_skip_recording;
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [recordingStarted, setRecordingStarted] = useState(false);

  // Show compliance modal when recording is required and story text should be hidden
  useEffect(() => {
    if (mustRecord && !controls.can_skip_recording && !recordingDone && !recordingStarted) {
      setShowComplianceModal(true);
    } else {
      setShowComplianceModal(false);
    }
  }, [mustRecord, controls.can_skip_recording, recordingDone, recordingStarted, currentChapter]);

  // Auto-open recorder if required
  useEffect(() => {
    if (mustRecord && (controls.auto_start_recording || !controls.can_skip_recording)) {
      setShowRecorder(true);
    }
  }, [mustRecord, currentChapter]);

  // Reset recording state when chapter changes
  useEffect(() => {
    setRecordingDone(false);
    setRecordingStarted(false);
    setRecordingBlobUrl(null);
    setShowRecorder(false);
    setHighlightedWordIndex(-1);
  }, [currentChapter]);

  // Save progress helper
  const saveProgress = React.useCallback(() => {
    if (currentNarrative?.id && student?.id) {
      narrativeProgressAPI.save({
        narrative_id: currentNarrative.id,
        student_id: student.id,
        current_chapter: currentChapter,
      }).catch(() => {});
    }
  }, [currentNarrative?.id, student?.id, currentChapter]);

  // Auto-save progress when chapter changes
  useEffect(() => {
    saveProgress();
  }, [saveProgress]);

  // Auto-save every 30 seconds while reading
  useEffect(() => {
    const autoSaveInterval = setInterval(saveProgress, 30000);
    return () => clearInterval(autoSaveInterval);
  }, [saveProgress]);

  // Save progress on browser close / back button / tab switch
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentNarrative?.id && student?.id) {
        // Use fetch with keepalive for reliable save during page unload
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_BACKEND_URL || '';
        const url = `${apiBase}/api/narratives/save-progress`;
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            narrative_id: currentNarrative.id,
            student_id: student.id,
            current_chapter: currentChapter,
          }),
          keepalive: true,
        }).catch(() => {});
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        saveProgress();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentNarrative?.id, student?.id, currentChapter, saveProgress]);

  const handleSaveAndExit = () => {
    if (currentNarrative?.id && student?.id) {
      narrativeProgressAPI.save({
        narrative_id: currentNarrative.id,
        student_id: student.id,
        current_chapter: currentChapter,
      }).then(() => {
        onClose();
      }).catch(() => onClose());
    } else {
      onClose();
    }
  };

  const handleTooHard = async () => {
    setDifficultyLoading(true);
    try {
      const res = await narrativeAPI.reportTooHard(currentNarrative.id);
      const data = res.data;
      if (data.already_easiest) {
        toast("You're already at the easiest level. Keep trying \u2014 you've got this!", { icon: '\uD83D\uDCAA' });
        setDifficultySubmitted('too_hard_easiest');
      } else if (data.new_narrative_id) {
        toast.success("We've created an easier version of this story for you!");
        // Fetch the new narrative and switch to it
        const newRes = await narrativeAPI.getById(data.new_narrative_id);
        setCurrentNarrative(newRes.data);
        setCurrentChapter(1);
        setCompletedChapters([]);
        setDifficultySubmitted('too_hard_switched');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setDifficultyLoading(false);
    }
  };

  const handleDifficultyFeedback = async (feedback) => {
    try {
      await narrativeAPI.difficultyFeedback(currentNarrative.id, feedback);
      setDifficultySubmitted(feedback);
      toast.success(feedback === 'just_right' ? 'Thanks for the feedback!' : 'Feedback recorded!');
    } catch {
      toast.error('Could not save feedback.');
    }
  };

  const chapter = currentNarrative.chapters[currentChapter - 1];
  const isLastChapter = currentChapter === 5;
  const isChapterCompleted = completedChapters.includes(currentChapter);
  const allChaptersCompleted = completedChapters.length === 5;

  // If story has all chapters completed but assessment wasn't done, show it
  useEffect(() => {
    if (allChaptersCompleted && currentNarrative.status !== 'completed' && !showAssessment) {
      // Check if an assessment already exists for this narrative
      import('@/lib/api').then(({ assessmentAPI }) => {
        assessmentAPI.getAll({ student_id: student.id, narrative_id: currentNarrative.id }).then(res => {
          const existing = res.data || [];
          const hasCompleted = existing.some(a => a.status === 'completed');
          if (!hasCompleted) {
            toast.success('All chapters read! Time for your vocabulary assessment!');
            setTimeout(() => setShowAssessment(true), 1000);
          }
        }).catch(() => {
          // If we can't check, show the assessment anyway
          toast.success('All chapters read! Time for your vocabulary assessment!');
          setTimeout(() => setShowAssessment(true), 1000);
        });
      });
    }
  }, [allChaptersCompleted, currentNarrative.status, currentNarrative.id, student.id]);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((new Date() - sessionStart) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [sessionStart]);

  // Heartbeat-based active reading time tracking. Only counts seconds when the
  // tab is visible AND the user has interacted recently — fixes the WPM skew
  // that happens when students leave the story open and walk away.
  useReadingHeartbeat({
    studentId: student?.id,
    narrativeId: currentNarrative?.id,
    chapterNumber: currentChapter,
    enabled: !showAssessment,
  });

  const createReadLogMutation = useMutation({
    mutationFn: (data) => readLogAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['student-detail']);
      queryClient.invalidateQueries(['read-logs']);
    },
  });

  const handleFinishChapter = () => {
    // If TTS is still playing when finishing, stop the pause timer
    if (ttsPauseStartRef.current) {
      readingPausedSecondsRef.current += (Date.now() - ttsPauseStartRef.current) / 1000;
      ttsPauseStartRef.current = null;
    }
    createReadLogMutation.mutate({
      student_id: student.id, narrative_id: currentNarrative.id,
      chapter_number: currentChapter, session_start: sessionStart.toISOString(),
      session_end: new Date().toISOString(), words_read: chapter.word_count,
      adjusted_reading_seconds: getAdjustedSeconds(),
    });
    setShowWrittenCheck(true);
  };

  const handleWrittenCheckComplete = (passed) => {
    setShowWrittenCheck(false);
    readLogAPI.create({
      student_id: student.id, narrative_id: currentNarrative.id,
      chapter_number: currentChapter, session_start: sessionStart.toISOString(),
      session_end: new Date().toISOString(), words_read: 0, vision_check_passed: passed,
    }).catch(() => {});

    if (passed) {
      const newCompleted = [...new Set([...completedChapters, currentChapter])];
      setCompletedChapters(newCompleted);

      // Queue the next action so we can show a reflection question first
      // (every other chapter, starting with chapter 2).
      const nextAction = () => {
        if (newCompleted.length === 5) {
          toast.success('Story completed! Time for vocabulary assessment!');
          setTimeout(() => setShowAssessment(true), 1500);
        } else if (!isLastChapter) {
          toast.success('Great job! Moving to next chapter...');
          setTimeout(() => setCurrentChapter((prev) => prev + 1), 1000);
        }
      };

      // Show reflection on even chapters and on the final chapter.
      const shouldReflect = currentChapter % 2 === 0 || newCompleted.length === 5;
      if (shouldReflect) {
        setPendingAdvance(() => nextAction);
        setShowReflection(true);
      } else {
        nextAction();
      }
    } else {
      toast.error('Try reading the chapter again more carefully!');
    }
  };

  const handleReflectionClose = () => {
    setShowReflection(false);
    if (pendingAdvance) {
      const fn = pendingAdvance;
      setPendingAdvance(null);
      fn();
    }
  };

  const getAdjustedSeconds = () => Math.max(1, elapsedSeconds - readingPausedSecondsRef.current);
  const calculateWPM = () => (elapsedSeconds === 0 ? 0 : Math.round(chapter.word_count / (getAdjustedSeconds() / 60)));

  const handleTTSPlayStateChange = (isNowPlaying) => {
    if (isNowPlaying) {
      ttsPauseStartRef.current = Date.now();
    } else if (ttsPauseStartRef.current) {
      readingPausedSecondsRef.current += (Date.now() - ttsPauseStartRef.current) / 1000;
      ttsPauseStartRef.current = null;
    }
  };
  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  if (showAssessment) {
    return <VocabularyAssessment narrative={currentNarrative} student={student} onClose={onClose} />;
  }

  return (
    <div className="min-h-screen sv-dark" style={{ background: readerBg.value, fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'background 0.4s ease' }}>
      {/* Sticky Header */}
      <header className="sticky top-0 z-50" style={{ background: C.surface, borderBottom: '1px solid rgba(212,168,83,0.15)' }}>
        <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3">
          {/* Top row: back button + title + timer */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button onClick={handleSaveAndExit} className="flex items-center gap-1 px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all hover:scale-105 flex-shrink-0"
                style={{ color: C.muted, border: '1px solid rgba(255,255,255,0.1)' }} data-testid="reader-back-btn">
                <ArrowLeft size={14} /> <span className="hidden sm:inline">Save & Exit</span>
              </button>
              <div className="min-w-0" style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '8px' }}>
                <h1 className="text-xs sm:text-sm font-bold truncate" style={{ color: C.cream }} data-testid="story-title">{currentNarrative.title}</h1>
                <p className="text-xs" style={{ color: C.muted }}>Ch. {currentChapter}/5</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="flex items-center gap-1.5 px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="reading-timer">
                <Clock size={12} style={{ color: '#34D399' }} className="animate-pulse" />
                <span className="font-mono font-bold text-xs sm:text-sm" style={{ color: C.cream }}>{formatTime(elapsedSeconds)}</span>
                {elapsedSeconds > 0 && (
                  <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full hidden sm:inline" style={{ background: 'rgba(52,211,153,0.12)', color: '#34D399' }}>
                    {calculateWPM()} WPM
                  </span>
                )}
              </div>
              {student.spellcheck_disabled && (
                <span className="text-xs font-semibold px-2 py-1 rounded-full hidden sm:inline-flex" style={{ background: 'rgba(245,158,11,0.12)', color: '#FBBF24' }} data-testid="spellcheck-off-badge">
                  <AlertTriangle size={10} className="inline mr-1" /> Off
                </span>
              )}
            </div>
          </div>

          {/* Controls row: background picker + save offline */}
          <div className="flex items-center justify-end gap-2 mt-2 relative">
            <button
              onClick={() => setShowBgPicker((v) => !v)}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-105"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: C.cream }}
              title="Change reading background"
              data-testid="reader-bg-picker-btn"
            >
              <Palette size={14} style={{ color: C.gold }} />
              <span className="hidden sm:inline">{readerBg.name}</span>
            </button>
            <SaveOfflineButton narrative={narrative} compact />

            {showBgPicker && (
              <>
                {/* Click-outside scrim */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowBgPicker(false)}
                  data-testid="reader-bg-picker-scrim"
                />
                <div
                  className="absolute right-0 top-full mt-2 z-50 p-3 rounded-xl shadow-2xl"
                  style={{ background: C.card, border: `1px solid ${C.gold}`, minWidth: '280px' }}
                  data-testid="reader-bg-picker-popover"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold uppercase" style={{ color: C.muted }}>Reading Background</span>
                    <button onClick={() => setShowBgPicker(false)} style={{ color: C.muted }} aria-label="Close picker">
                      <XIcon size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {READER_BACKGROUNDS.map((bg) => {
                      const isSelected = bg.id === readerBg.id;
                      return (
                        <button
                          key={bg.id}
                          onClick={() => { setReaderBg(bg); setShowBgPicker(false); }}
                          className="flex items-center gap-2 p-1.5 rounded-lg text-left transition-all hover:scale-[1.02]"
                          style={{
                            background: isSelected ? 'rgba(212,168,83,0.15)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${isSelected ? C.gold : 'rgba(255,255,255,0.08)'}`,
                          }}
                          data-testid={`reader-bg-option-${bg.id}`}
                        >
                          <span
                            className="block w-8 h-8 rounded-md flex-shrink-0"
                            style={{ background: bg.value, border: '1px solid rgba(0,0,0,0.3)' }}
                          />
                          <span className="text-xs font-semibold truncate" style={{ color: C.cream }}>{bg.name}</span>
                          {isSelected && <CheckCircle size={12} style={{ color: C.gold, marginLeft: 'auto' }} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Progress bar */}
          <div className="mt-2 w-full h-1 sm:h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(currentChapter / 5) * 100}%`, background: `linear-gradient(90deg, ${C.gold}, ${C.teal})` }} />
          </div>
        </div>
      </header>

      {/* Reading Content */}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-3xl">
        <div className="p-4 sm:p-8 rounded-2xl" style={{ background: C.card, border: '1px solid rgba(255,255,255,0.06)' }}>
          {/* Chapter Title */}
          <div className="mb-6 sm:mb-8 pb-4 sm:pb-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <h2 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif", color: C.cream }}>{chapter.title}</h2>
              {isChapterCompleted && (
                <span className="flex items-center gap-1 text-xs font-semibold px-2 sm:px-3 py-1 rounded-full flex-shrink-0" style={{ background: 'rgba(52,211,153,0.12)', color: '#34D399' }}>
                  <CheckCircle size={12} /> Done
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs" style={{ color: C.muted }}>
              <span>{chapter.word_count} words</span>
              <span>~{Math.ceil(chapter.word_count / 200)} min</span>
            </div>
          </div>

          {/* Difficulty Feedback Panel — only on chapter 1 */}
          {currentChapter === 1 && !difficultySubmitted && (
            <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              data-testid="difficulty-panel">
              <span className="text-xs font-semibold mr-1" style={{ color: C.muted }}>How is this story?</span>
              <button
                onClick={handleTooHard}
                disabled={difficultyLoading}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105 disabled:opacity-50"
                style={{ background: 'rgba(239,68,68,0.12)', color: '#F87171', border: '1px solid rgba(239,68,68,0.25)' }}
                data-testid="too-hard-btn">
                {difficultyLoading ? <Loader2 size={12} className="animate-spin" /> : <span>{'\uD83D\uDE30'}</span>}
                {difficultyLoading ? 'Creating easier story...' : 'Too hard'}
              </button>
              <button
                onClick={() => handleDifficultyFeedback('just_right')}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105"
                style={{ background: 'rgba(52,211,153,0.12)', color: '#34D399', border: '1px solid rgba(52,211,153,0.25)' }}
                data-testid="just-right-btn">
                <span>{'\uD83D\uDE0A'}</span> Just right
              </button>
              <button
                onClick={() => handleDifficultyFeedback('too_easy')}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105"
                style={{ background: 'rgba(56,189,248,0.12)', color: '#38BDF8', border: '1px solid rgba(56,189,248,0.25)' }}
                data-testid="too-easy-btn">
                <span>{'\uD83D\uDE80'}</span> Too easy
              </button>
            </div>
          )}
          {currentChapter === 1 && difficultySubmitted && (
            <div className="flex items-center gap-2 mb-4 p-2 rounded-lg"
              style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.15)' }}
              data-testid="difficulty-thanks">
              <CheckCircle size={14} style={{ color: '#34D399' }} />
              <span className="text-xs font-semibold" style={{ color: '#34D399' }}>
                {difficultySubmitted === 'too_hard_switched' ? 'Switched to an easier story!' :
                 difficultySubmitted === 'too_hard_easiest' ? "You're at the easiest level already." :
                 'Thanks for your feedback!'}
              </span>
            </div>
          )}

          {/* Listen to Story — TTS Player only renders when the parent has
              explicitly enabled audio narration in Reading Rules. Native
              English speakers default to OFF because listening replaces
              reading; ESL learners and accessibility needs trigger ON. */}
          {student?.tts_enabled === true && (
            <TTSPlayer narrativeId={currentNarrative.id} chapterNumber={currentChapter} chapterText={chapter.content}
              voice={student?.tts_voice || 'rachel'}
              onWordChange={setHighlightedWordIndex} onPlayStateChange={handleTTSPlayStateChange} />
          )}

          {/* Illustration - only if enabled in reading rules */}
          {student?.illustrations_enabled && (
            <IllustrationPanel narrativeId={currentNarrative.id} chapterNumber={currentChapter} hasPicture={!!(chapter.illustration_description || chapter.illustration_url)} />
          )}

          {/* Recording Compliance Modal */}
          {showComplianceModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black" style={{ backdropFilter: 'blur(20px)' }}
              data-testid="recording-compliance-modal">
              <div className="w-full max-w-md p-6 rounded-2xl text-center" style={{ background: C.card, border: `2px solid ${C.gold}` }}>
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
                  {controls.recording_mode === 'audio_video'
                    ? <Video size={32} style={{ color: '#818CF8' }} />
                    : <Mic size={32} style={{ color: '#818CF8' }} />}
                </div>
                <h3 className="text-xl font-black mb-2" style={{ color: C.cream }}>Recording Required</h3>
                <p className="text-sm mb-4" style={{ color: C.muted }}>
                  {controls.recording_mode === 'audio_video'
                    ? 'Your parent requires you to record audio and video while reading this chapter. Please turn on your camera and microphone, then start recording before you can read.'
                    : 'Your parent requires you to record your voice while reading this chapter. Please start recording before you can read.'}
                </p>
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase" style={{ color: '#818CF8' }}>Instructions:</p>
                  <div className="text-left space-y-2 text-xs" style={{ color: C.muted }}>
                    <p>1. Click "Start Recording" below</p>
                    <p>2. Read the chapter out loud clearly</p>
                    <p>3. Click "Stop" when finished reading</p>
                    <p>4. Click "Analyze Diction" to submit</p>
                    <p>5. Story text will appear once recording begins</p>
                  </div>
                  <button onClick={() => { setShowComplianceModal(false); setRecordingStarted(true); setShowRecorder(true); }}
                    className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                    style={{ background: '#818CF8', color: 'white' }}
                    data-testid="comply-recording-btn">
                    {controls.recording_mode === 'audio_video'
                      ? <><Video size={16} /> I'm Ready — Start Recording</>
                      : <><Mic size={16} /> I'm Ready — Start Recording</>}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Read Aloud Section - only shown when recording is required */}
          {mustRecord && (
            <div className="mb-6">
              {!showRecorder ? (
                <button onClick={() => setShowRecorder(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition-all hover:scale-[1.01]"
                  style={{
                    background: !recordingDone ? 'rgba(99,102,241,0.15)' : 'rgba(52,211,153,0.1)',
                    color: !recordingDone ? '#818CF8' : '#34D399',
                    border: !recordingDone ? '1px solid rgba(99,102,241,0.3)' : '1px solid rgba(52,211,153,0.25)',
                  }}
                  data-testid="read-aloud-btn">
                  {!recordingDone ? <Lock size={16} /> : <Mic size={16} />}
                  {!recordingDone ? 'Record Before Reading (Required)' : 'Recording Complete'}
                </button>
              ) : (
                <div>
                  <p className="text-xs font-bold uppercase mb-2" style={{ color: C.gold }}>Required Recording</p>
                  <ReadAloudRecorder
                    key={`recorder-${currentNarrative.id}-${currentChapter}`}
                    studentId={student.id}
                    narrativeId={currentNarrative.id}
                    chapterNumber={currentChapter}
                    requiredMode={controls.recording_mode}
                    onRecordingComplete={(result) => {
                      setRecordingDone(true);
                      if (result.blob_url) setRecordingBlobUrl(result.blob_url);
                      toast.success(`Diction score: ${result.diction_scores?.overall}%`);
                    }}
                  />
                </div>
              )}

              {/* Recording playback after completion */}
              {recordingDone && recordingBlobUrl && (
                <div className="mt-3 p-3 rounded-xl" style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.25)' }}
                  data-testid="recording-playback">
                  <p className="text-xs font-bold mb-2" style={{ color: '#34D399' }}>Your Recording</p>
                  <audio controls src={recordingBlobUrl} className="w-full h-8" style={{ filter: 'invert(0.85)' }} />
                </div>
              )}
            </div>
          )}

          {/* Story Text */}
          <div className="mb-6 sm:mb-8" style={{
            filter: mustRecord && !recordingStarted ? 'blur(8px)' : 'none',
            userSelect: mustRecord && !recordingStarted ? 'none' : 'auto',
            pointerEvents: mustRecord && !recordingStarted ? 'none' : 'auto',
            ...(dyslexiaMode ? {
              background: dyslexiaOverlay,
              borderRadius: '12px',
              padding: '20px',
            } : {}),
          }}>
            <div className="text-base sm:text-lg leading-[1.8] sm:leading-[1.9] font-medium" style={{
              color: dyslexiaMode ? '#2D2D2D' : C.reading,
              ...(dyslexiaMode ? {
                fontFamily: "'OpenDyslexic', 'Comic Sans MS', sans-serif",
                fontSize: '20px',
                letterSpacing: '0.12em',
                wordSpacing: '0.25em',
                lineHeight: '2.0',
                maxWidth: '650px',
                textAlign: 'left',
              } : {}),
            }}>
              {(() => {
                // Build a global word index counter for TTS highlighting
                // Clean text words (same as TTS uses) for matching
                const cleanTextForTTS = (chapter.content || '').replace(/\[MEDIA:[^\]]+\]/g, '');
                const ttsWords = cleanTextForTTS.split(/\s+/).filter(Boolean);
                let globalWordIdx = 0;

                return chapter.content.split(/(\[MEDIA:[^\]]+\])/).map((part, pIdx) => {
                  // Check for media tag [MEDIA:id:title]
                  const mediaMatch = part.match(/^\[MEDIA:([^:]+):([^\]]+)\]$/);
                  if (mediaMatch) {
                    return <InlineMediaPlayer key={`media-${pIdx}`} mediaId={mediaMatch[1]} title={mediaMatch[2]}
                      mediaPlacements={currentNarrative.media_placements || []} studentId={student?.id} />;
                  }
                  // Regular text — render words with click-to-define and TTS highlighting
                  return <React.Fragment key={`part-${pIdx}`}>{part.split(/(\s+)/).map((segment, idx) => {
                    const cleanWord = segment.replace(/[^a-zA-Z'-]/g, '');
                    if (!cleanWord || cleanWord.length < 2) return <span key={idx}>{segment}</span>;
                    const thisWordIdx = globalWordIdx++;
                    const isHighlighted = highlightedWordIndex >= 0 && thisWordIdx === highlightedWordIndex;
                    return (
                      <span key={idx}
                        className="cursor-pointer transition-colors rounded px-[1px]"
                        style={{
                          borderBottom: '1px solid transparent',
                          background: isHighlighted ? 'rgba(212,168,83,0.35)' : 'transparent',
                          borderRadius: isHighlighted ? '3px' : undefined,
                        }}
                        onMouseEnter={(e) => { e.target.style.borderBottomColor = C.gold; e.target.style.color = C.gold; }}
                        onMouseLeave={(e) => { e.target.style.borderBottomColor = 'transparent'; e.target.style.color = isHighlighted ? C.gold : C.reading; }}
                        onClick={() => {
                          setSelectedWord(cleanWord);
                          const words = chapter.content.split(/\s+/);
                          const wIdx = words.findIndex((w) => w.includes(cleanWord));
                          const start = Math.max(0, wIdx - 5);
                          const end = Math.min(words.length, wIdx + 6);
                          setWordContext(words.slice(start, end).join(' '));
                        }}
                        data-testid={`word-${idx}`}>
                        {segment}
                      </span>
                    );
                  })}</React.Fragment>;
                });
              })()}
            </div>
            <p className="text-xs mt-3 italic" style={{ color: C.muted }}>Tap any word to see its definition</p>
          </div>


          {/* Controls */}
          <div className="space-y-3">
            {/* Block warning if recording required */}
            {mustRecord && !recordingDone && (
              <div className="flex items-center gap-2 p-3 rounded-xl"
                style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)' }}
                data-testid="recording-block-warning">
                <Lock size={16} style={{ color: '#FBBF24' }} />
                <p className="text-xs font-bold" style={{ color: '#FBBF24' }}>
                  Complete the recording above before you can finish this chapter.
                </p>
              </div>
            )}

            {!isChapterCompleted && (
              <button onClick={handleFinishChapter}
                disabled={mustRecord && !recordingDone}
                className="w-full py-3 sm:py-3.5 rounded-xl text-sm sm:text-base font-bold text-black transition-all hover:scale-[1.01] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: recordingDone ? 'linear-gradient(135deg, #34D399, #6EE7B7)' : `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, fontWeight: recordingDone ? 900 : 700 }} data-testid="finish-chapter-btn">
                <CheckCircle size={16} className="inline mr-2" /> {recordingDone ? 'Continue to Next Chapter' : 'Finish Chapter & Answer Question'}
              </button>
            )}
            {isChapterCompleted && (
              <div className="flex gap-2 sm:gap-3">
                {currentChapter > 1 && (
                  <button onClick={() => setCurrentChapter((p) => p - 1)}
                    className="flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-3 rounded-xl text-sm font-semibold transition-all"
                    style={{ color: C.muted, border: '1px solid rgba(255,255,255,0.1)' }}>
                    <ArrowLeft size={14} /> <span className="hidden sm:inline">Previous</span>
                  </button>
                )}
                {!isLastChapter ? (
                  <button onClick={() => setCurrentChapter((p) => p + 1)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm sm:text-base font-bold text-black transition-all hover:scale-[1.01]"
                    style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` }}>
                    Next Chapter <ArrowRight size={14} />
                  </button>
                ) : (
                  <button onClick={onClose}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm sm:text-base font-bold text-black transition-all hover:scale-[1.01]"
                    style={{ background: `linear-gradient(135deg, #34D399, #6EE7B7)` }}>
                    <CheckCircle size={16} /> Complete Story
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showWrittenCheck && (
        <WrittenAnswerModal question={chapter.vision_check?.question || `What was the main idea of ${chapter.title}?`}
          chapterNumber={currentChapter} chapterContent={chapter.content} student={student}
          onComplete={handleWrittenCheckComplete} />
      )}
      {showReflection && (
        <ReflectionQuestionModal
          studentId={student?.id}
          narrativeId={currentNarrative?.id}
          onClose={handleReflectionClose}
        />
      )}
      {selectedWord && (
        <WordDefinitionModal word={selectedWord} context={wordContext}
          onClose={() => { setSelectedWord(null); setWordContext(''); }} />
      )}

    </div>
  );
};

export default NarrativeReader;
